/**
 * 产品数据层 —— 唯一数据源是根目录 data/products.json（31 个型号，逐字保留）。
 * 不要在此文件修改任何产品字段；产品内容以 data/products.json 为准。
 */
import rawProducts from '../../data/products.json';

export type ProductModel = {
  id: string;
  name: string;
  category: string;
  axis: number;
  accuracy: string;
  interface: string;
  temp: string;
  image: string;
  datasheet_pdf: string;
  url: string;
  description: string;
  specs: Record<string, string>;
};

export const products = rawProducts as unknown as ProductModel[];

/** 产品类别元数据 —— 文案逐字提取自原静态页面 products/*.html */
export type Category = {
  slug: string;
  title: string;
  tagline: string;
  description: string;
  image: string;
};

export const categories: Category[] = [
  {
    slug: 'fiber-optic-gyroscope',
    title: 'Fiber Optic Gyroscopes (FOG)',
    tagline: 'Zero moving parts. Sagnac-effect precision. Proven reliability.',
    description:
      'Our fiber optic gyroscopes leverage the Sagnac effect for rotation sensing with zero moving parts. Available in single-axis, dual-axis, and tri-axis configurations with bias stability from 0.003°/h (strategic grade) to 2°/h (industrial grade). Ideal for INS, platform stabilization, north-finding, and precision pointing.',
    image: '/images/products/GN1-FOG-70.png'
  },
  {
    slug: 'quartz-accelerometer',
    title: 'Quartz Flexible Accelerometers',
    tagline: 'Frictionless quartz flexure. Micro-g precision. Extreme environments.',
    description:
      'Our quartz flexible accelerometers use a precision quartz flexure suspension for frictionless operation. Available with bias stability from 1μg to 15μg, including specialized high-temperature models rated to +180°C for downhole drilling. Applications include satellite microgravity, missile INS, oil & gas MWD, and seismic monitoring.',
    image: '/images/products/GN1-QFA-05.png'
  },
  {
    slug: 'mems-imu',
    title: 'MEMS IMU & Sensors',
    tagline: 'Compact MEMS. Proven precision. From consumer to tactical grade.',
    description:
      'Our MEMS product line includes triaxial accelerometers (GN1/GN2/GN3 series), single and triaxial gyroscopes, and fully integrated 6-DOF IMU modules. Leveraging advanced silicon micromachining and proven MEMS sensing technology, these sensors deliver reliable performance from consumer-grade to tactical-grade applications. Optimized for size, weight, and power (SWaP)-constrained platforms including UAVs, robotics, and portable navigation systems.',
    image: '/images/products/GN3-MEMS-G-50.png'
  },
  {
    slug: 'inertial-navigation-system',
    title: 'Inertial Navigation Systems (INS)',
    tagline: 'Integrated INS. Sensor fusion. From miniature MEMS to strategic-grade FOG.',
    description:
      'Our integrated inertial navigation systems combine gyroscopes and accelerometers with advanced sensor fusion algorithms for complete 6-DOF navigation solutions. MEMS-based and FOG-based options are available, with optional GNSS integration for absolute positioning. From 70g miniature IMUs for UAVs to strategic-grade rack-mount systems for maritime and land navigation.',
    image: '/images/products/GN3-IMU1-70A.png'
  }
];

export function productsByCategory(categorySlug: string): ProductModel[] {
  return products.filter((p) => p.category === categorySlug);
}

export function getProduct(productId: string): ProductModel | undefined {
  return products.find((p) => p.id === productId);
}

export function getCategory(categorySlug: string): Category | undefined {
  return categories.find((c) => c.slug === categorySlug);
}

/** 型号名（如 GN1-FOG-50）—— 从 name 字段截取，用于卡片标题 */
export function modelId(name: string): string {
  return name.split(' ')[0];
}
